import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Save, Sparkles, Tag, Package, Gift, 
  HelpCircle, MessageSquare, Plus, Trash2, CheckCircle2, 
  Eye, EyeOff, Star, ShieldCheck, Flame
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import RichTextEditor from '../../components/ui/RichTextEditor';
import ProductImageUploader from '../../components/ui/ProductImageUploader';
import { toast } from 'sonner';

import API from '../../config/api';

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-');
};

const AdminProductAdd = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('en'); // 'en' or 'hi'
  const [isCustomSlug, setIsCustomSlug] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    category_name: '',
    price: '',
    sale_price: '',
    discount: '',
    stock: 100,
    origin: 'Haridwar, Uttarakhand',
    
    // Bilingual Content
    description_en: '',
    description_hi: '',
    benefits_en: '',
    benefits_hi: '',
    how_to_use_en: '',
    how_to_use_hi: '',
    usp_en: '',
    usp_hi: '',

    // Images
    image_url: '',
    images: [],

    // Badges & Visibility
    is_active: true,
    bestseller: false,

    // Gift with Purchase
    gift_product_id: '',

    // Sacred Vedic Mantra (Optional)
    mantra: '',
    mantra_translit: '',
    mantra_note: '',

    // FAQs and Testimonials (Optional - leave empty for global fallback)
    faqs: [],
    testimonials: []
  });

  // Fetch categories and products for the gift selector
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          axios.get(`${API}/categories?all=true`),
          axios.get(`${API}/products?limit=100&include_inactive=true`)
        ]);

        const catList = Array.isArray(catRes.data) ? catRes.data : [];
        setCategories(catList);
        if (catList.length > 0) {
          setFormData(prev => (prev.category ? prev : {
            ...prev,
            category: catList[0].slug,
            category_name: catList[0].name
          }));
        }

        const prodList = prodRes.data?.products || (Array.isArray(prodRes.data) ? prodRes.data : []);
        setAllProducts(prodList);

      } catch (err) {
        console.error('Failed to load initial data:', err);
      }
    };
    fetchData();
  }, []);

  // Auto-generate slug WordPress-style on name change (if not manually edited)
  const handleNameChange = (e) => {
    const nameVal = e.target.value;
    setFormData(prev => ({
      ...prev,
      name: nameVal,
      slug: isCustomSlug ? prev.slug : slugify(nameVal)
    }));
  };

  const handleSlugChange = (e) => {
    setIsCustomSlug(true);
    setFormData(prev => ({
      ...prev,
      slug: slugify(e.target.value)
    }));
  };

  // Auto-calculate discount % when price or sale_price changes
  const handlePriceChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    const regPrice = parseFloat(field === 'price' ? value : formData.price);
    const salePrice = parseFloat(field === 'sale_price' ? value : formData.sale_price);

    if (regPrice > 0 && salePrice > 0 && salePrice < regPrice) {
      updated.discount = Math.round(((regPrice - salePrice) / regPrice) * 100);
    } else if (field === 'price' && (!salePrice || salePrice >= regPrice)) {
      updated.discount = 0;
    }
    setFormData(updated);
  };

  // FAQ Handlers
  const handleAddFaq = () => {
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const handleUpdateFaq = (index, key, val) => {
    const updated = [...formData.faqs];
    updated[index][key] = val;
    setFormData(prev => ({ ...prev, faqs: updated }));
  };

  const handleRemoveFaq = (index) => {
    setFormData(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, idx) => idx !== index)
    }));
  };

  // Testimonial Handlers
  const handleAddTestimonial = () => {
    setFormData(prev => ({
      ...prev,
      testimonials: [...prev.testimonials, { name: '', location: '', rating: 5, quote: '', verified: true }]
    }));
  };

  const handleUpdateTestimonial = (index, key, val) => {
    const updated = [...formData.testimonials];
    updated[index][key] = val;
    setFormData(prev => ({ ...prev, testimonials: updated }));
  };

  const handleRemoveTestimonial = (index) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, idx) => idx !== index)
    }));
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    if (!formData.price) {
      toast.error('Please enter regular price');
      return;
    }
    if (!formData.image_url && formData.images.length === 0) {
      toast.error('Please upload at least one featured product image');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        discount: formData.discount ? parseInt(formData.discount) : 0,
        stock: parseInt(formData.stock) || 0,
        image_url: formData.image_url || formData.images[0],
        images: formData.images.length > 0 ? formData.images : [formData.image_url]
      };

      const res = await axios.post(`${API}/products`, payload, { withCredentials: true });
      toast.success('Product created successfully!');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 pb-20">
      
      {/* ─── Top Action Header ─── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-[#1A1A1A]/[0.08] shadow-xs">
        <div className="flex items-center gap-3">
          <Link to="/admin/products">
            <button
              type="button"
              className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#F3E7D0] flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          </Link>
          <div>
            <h1 className="font-heading text-xl font-bold text-gray-900">Add New Product</h1>
            <p className="text-xs text-gray-500">Create a sacred offering for the divine store</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link to="/admin/products">
            <Button variant="outline" type="button" className="text-xs font-semibold">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            disabled={submitting}
            className="btn-saffron text-xs font-bold px-6 py-2 rounded-xl flex items-center gap-2 shadow-md"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Publish Product</span>
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* ═══════════════════ LEFT COLUMN: MAIN DETAILS (8 cols) ═══════════════════ */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* 1. Basic Product Info Card */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <Package className="w-4 h-4 text-[#D9711F]" />
              Basic Product Information
            </h2>

            {/* Product Name */}
            <div>
              <Label htmlFor="product-name" className="text-xs font-bold text-gray-800">
                Product Name *
              </Label>
              <Input
                id="product-name"
                value={formData.name}
                onChange={handleNameChange}
                placeholder="e.g. Metal Dhan Yog Bracelet for Women"
                className="mt-1 font-medium text-sm"
                required
              />
            </div>

            {/* Slug (WordPress-style auto-generated, editable) */}
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="product-slug" className="text-xs font-bold text-gray-800">
                  URL Slug (Unique Link Identifier) *
                </Label>
                <span className="text-[11px] text-gray-400">Auto-generated from name</span>
              </div>
              <div className="flex items-center mt-1 border border-gray-200 rounded-xl overflow-hidden focus-within:border-[#D9711F] bg-gray-50/50">
                <span className="px-3 text-xs text-gray-500 bg-gray-100/70 border-r border-gray-200 py-2.5 select-none font-mono">
                  /store/
                </span>
                <input
                  id="product-slug"
                  type="text"
                  value={formData.slug}
                  onChange={handleSlugChange}
                  placeholder="metal-dhan-yog-bracelet-for-women"
                  className="flex-1 bg-transparent px-3 py-2 text-xs font-mono text-[#2A1F16] focus:outline-none"
                  required
                />
              </div>
            </div>

            {/* Origin & Category Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <Label htmlFor="product-category" className="text-xs font-bold text-gray-800">
                  Category *
                </Label>
                <select
                  id="product-category"
                  value={formData.category}
                  onChange={(e) => {
                    const selectedCat = categories.find(c => c.slug === e.target.value || c.id === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      category: e.target.value,
                      category_name: selectedCat?.name || e.target.value
                    }));
                  }}
                  className="w-full mt-1 border border-gray-200 rounded-xl px-3 py-2 text-xs font-medium text-gray-800 bg-white focus:outline-none focus:border-[#D9711F]"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.slug || cat.id}>
                      {cat.name} {cat.badge ? `(${cat.badge})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="product-origin" className="text-xs font-bold text-gray-800">
                  Sacred Origin / Source
                </Label>
                <Input
                  id="product-origin"
                  value={formData.origin}
                  onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                  placeholder="e.g. Haridwar, Uttarakhand or Nepal"
                  className="mt-1 text-xs"
                />
              </div>
            </div>
          </div>

          {/* 2. Direct Image Upload Box (Point 2) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-3">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D9711F]" />
              Product Media &amp; Gallery (Max 10 Images)
            </h2>

            <ProductImageUploader
              featuredImage={formData.image_url}
              galleryImages={formData.images}
              onFeaturedChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
              onGalleryChange={(urls) => setFormData(prev => ({ ...prev, images: urls }))}
            />
          </div>

          {/* 3. Bilingual Rich Text Content Editor (Points 1 & 3) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-6">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                <Tag className="w-4 h-4 text-[#D9711F]" />
                Rich Content &amp; Descriptions (Bilingual)
              </h2>

              {/* Language Switcher Tabs */}
              <div className="flex bg-[#FBF4E7] p-1 rounded-xl border border-[#E7D9BC]">
                <button
                  type="button"
                  onClick={() => setActiveTab('en')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                    activeTab === 'en'
                      ? 'bg-[#6E1B34] text-white shadow-xs'
                      : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  🇬🇧 English Content
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('hi')}
                  className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                    activeTab === 'hi'
                      ? 'bg-[#6E1B34] text-white shadow-xs'
                      : 'text-[#83725B] hover:text-[#2A1F16]'
                  }`}
                >
                  🇮🇳 Hindi Content (हिंदी)
                </button>
              </div>
            </div>

            {/* English Fields */}
            {activeTab === 'en' ? (
              <div className="space-y-6">
                <RichTextEditor
                  label="English Product Description"
                  value={formData.description_en}
                  onChange={(val) => setFormData(prev => ({ ...prev, description_en: val }))}
                  placeholder="Detailed spiritual and material description in English..."
                  helpText="Describe the craftsmanship, material purity, and spiritual significance."
                />

                <RichTextEditor
                  label="English Benefits &amp; Astrological Significance"
                  value={formData.benefits_en}
                  onChange={(val) => setFormData(prev => ({ ...prev, benefits_en: val }))}
                  placeholder="Bullet points of planetary and financial benefits..."
                  helpText="List the sacred benefits (planetary alignment, prosperity, focus)."
                />

                <RichTextEditor
                  label="English How to Use &amp; Sacred Rituals"
                  value={formData.how_to_use_en}
                  onChange={(val) => setFormData(prev => ({ ...prev, how_to_use_en: val }))}
                  placeholder="Step-by-step wearing and care instructions..."
                  helpText="Guidance on which wrist to wear, mantra chanting count, and cleaning."
                />

                <RichTextEditor
                  label="English Unique Selling Proposition (USP)"
                  value={formData.usp_en}
                  onChange={(val) => setFormData(prev => ({ ...prev, usp_en: val }))}
                  placeholder="Unique selling points, authenticity certifications, distinctive spiritual features..."
                  helpText="Highlight the distinctive USP that makes this sacred offering stand out."
                />
              </div>
            ) : (
              /* Hindi Fields */
              <div className="space-y-6">
                <RichTextEditor
                  label="Hindi Product Description (हिंदी विवरण)"
                  value={formData.description_hi}
                  onChange={(val) => setFormData(prev => ({ ...prev, description_hi: val }))}
                  placeholder="उत्पाद का आध्यात्मिक एवं भौतिक विवरण हिंदी में लिखें..."
                />

                <RichTextEditor
                  label="Hindi Benefits &amp; Significance (महत्व एवं लाभ)"
                  value={formData.benefits_hi}
                  onChange={(val) => setFormData(prev => ({ ...prev, benefits_hi: val }))}
                  placeholder="धार्मिक व ज्योतिषीय लाभों के बिंदु हिंदी में लिखें..."
                />

                <RichTextEditor
                  label="Hindi How to Use (धारण करने की विधि व नियम)"
                  value={formData.how_to_use_hi}
                  onChange={(val) => setFormData(prev => ({ ...prev, how_to_use_hi: val }))}
                  placeholder="धारण करने की शुभ विधि, दिन और सावधानियां..."
                />

                <RichTextEditor
                  label="Hindi Unique Selling Proposition (USP - मुख्य विशेषताएं)"
                  value={formData.usp_hi}
                  onChange={(val) => setFormData(prev => ({ ...prev, usp_hi: val }))}
                  placeholder="उत्पाद की मुख्य विशेषताएं व विशिष्टता (USP) हिंदी में लिखें..."
                />
              </div>
            )}
          </div>

          {/* 4. Sacred Vedic Mantra (Optional - Point 13) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <Flame className="w-4 h-4 text-[#D9711F]" />
              Sacred Vedic Mantra (Optional)
            </h2>

            <div>
              <Label htmlFor="product-mantra" className="text-xs font-bold text-gray-800">
                Mantra in Devanagari Sanskrit
              </Label>
              <Input
                id="product-mantra"
                value={formData.mantra}
                onChange={(e) => setFormData({ ...formData, mantra: e.target.value })}
                placeholder="ॐ श्रीं ह्रीं श्रीं कमले कमलालये प्रसीद प्रसीद..."
                className="mt-1 font-deva text-base"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="product-mantra-translit" className="text-xs font-bold text-gray-800">
                  Mantra Transliteration / Romanized
                </Label>
                <Input
                  id="product-mantra-translit"
                  value={formData.mantra_translit}
                  onChange={(e) => setFormData({ ...formData, mantra_translit: e.target.value })}
                  placeholder="Om Shreem Hreem Shreem..."
                  className="mt-1 text-xs italic"
                />
              </div>

              <div>
                <Label htmlFor="product-mantra-note" className="text-xs font-bold text-gray-800">
                  Chanting Instruction Note
                </Label>
                <Input
                  id="product-mantra-note"
                  value={formData.mantra_note}
                  onChange={(e) => setFormData({ ...formData, mantra_note: e.target.value })}
                  placeholder="Chant 11 or 108 times at sunrise..."
                  className="mt-1 text-xs"
                />
              </div>
            </div>
          </div>

          {/* 5. FAQs Builder (Optional) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div>
                <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-[#D9711F]" />
                  Product FAQs (Optional)
                </h2>
                <p className="text-[11px] text-gray-500">
                  Leave empty to automatically use the global site-wide FAQs.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddFaq}
                className="text-xs text-[#6E1B34] hover:text-[#D9711F] font-bold inline-flex items-center gap-1 bg-[#FBF4E7] px-3 py-1.5 rounded-lg border border-[#E7D9BC]"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Custom FAQ
              </button>
            </div>

            {formData.faqs.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center">
                <p className="text-xs text-gray-500 font-medium">No custom FAQs added for this product.</p>
                <p className="text-[11px] text-gray-400 mt-0.5">This product will display the global FAQs managed in Admin &rarr; FAQs.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.faqs.map((faq, index) => (
                  <div key={index} className="p-4 bg-[#FBF4E7]/40 rounded-xl border border-[#E7D9BC] space-y-2 relative">
                    <button
                      type="button"
                      onClick={() => handleRemoveFaq(index)}
                      className="absolute top-3 right-3 text-red-400 hover:text-red-600"
                      title="Remove Question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div>
                      <Label className="text-[11px] font-bold text-gray-700">Question #{index + 1}</Label>
                      <Input
                        value={faq.question}
                        onChange={(e) => handleUpdateFaq(index, 'question', e.target.value)}
                        placeholder="e.g. Has this product been energised before shipping?"
                        className="mt-0.5 text-xs bg-white"
                      />
                    </div>

                    <div>
                      <Label className="text-[11px] font-bold text-gray-700">Answer</Label>
                      <textarea
                        value={faq.answer}
                        onChange={(e) => handleUpdateFaq(index, 'answer', e.target.value)}
                        placeholder="e.g. Yes, every product undergoes Prana Pratishtha by Himalayan pandits..."
                        rows={2}
                        className="w-full mt-0.5 border border-gray-200 rounded-xl p-2.5 text-xs text-[#2A1F16] bg-white focus:outline-none focus:border-[#D9711F]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 6. Testimonials Builder (Optional) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div>
                <h2 className="font-heading text-base font-bold text-gray-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#D9711F]" />
                  Customer Reviews &amp; Testimonials (Optional)
                </h2>
                <p className="text-[11px] text-gray-500">
                  Leave empty to automatically use the global site-wide Testimonials.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddTestimonial}
                className="text-xs text-[#6E1B34] hover:text-[#D9711F] font-bold inline-flex items-center gap-1 bg-[#FBF4E7] px-3 py-1.5 rounded-lg border border-[#E7D9BC]"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Custom Review
              </button>
            </div>

            {formData.testimonials.length === 0 ? (
              <div className="p-4 bg-gray-50 rounded-xl border border-dashed border-gray-200 text-center">
                <p className="text-xs text-gray-500 font-medium">No custom reviews added for this product.</p>
                <p className="text-[11px] text-gray-400 mt-0.5">This product will display the global customer reviews managed in Admin &rarr; Testimonials.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.testimonials.map((testi, index) => (
                  <div key={index} className="p-4 bg-[#FBF4E7]/40 rounded-xl border border-[#E7D9BC] space-y-2 relative">
                    <button
                      type="button"
                      onClick={() => handleRemoveTestimonial(index)}
                      className="absolute top-3 right-3 text-red-400 hover:text-red-600"
                      title="Remove Review"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[11px] font-bold text-gray-700">Customer Name</Label>
                        <Input
                          value={testi.name}
                          onChange={(e) => handleUpdateTestimonial(index, 'name', e.target.value)}
                          placeholder="e.g. Priya S."
                          className="mt-0.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-bold text-gray-700">Location</Label>
                        <Input
                          value={testi.location}
                          onChange={(e) => handleUpdateTestimonial(index, 'location', e.target.value)}
                          placeholder="e.g. Pune, Maharashtra"
                          className="mt-0.5 text-xs bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] font-bold text-gray-700">Rating</Label>
                        <select
                          value={testi.rating}
                          onChange={(e) => handleUpdateTestimonial(index, 'rating', Number(e.target.value))}
                          className="w-full mt-0.5 border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white focus:outline-none focus:border-[#D9711F]"
                        >
                          <option value={5}>⭐⭐⭐⭐⭐ (5 Stars)</option>
                          <option value={4}>⭐⭐⭐⭐ (4 Stars)</option>
                          <option value={3}>⭐⭐⭐ (3 Stars)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <Label className="text-[11px] font-bold text-gray-700">Review Feedback</Label>
                      <textarea
                        value={testi.quote}
                        onChange={(e) => handleUpdateTestimonial(index, 'quote', e.target.value)}
                        placeholder="What did the customer say about this item..."
                        rows={2}
                        className="w-full mt-0.5 border border-gray-200 rounded-xl p-2.5 text-xs text-[#2A1F16] bg-white focus:outline-none focus:border-[#D9711F]"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* ═══════════════════ RIGHT COLUMN: PRICING, STOCK, BADGES, GIFT (4 cols) ═══════════════════ */}
        <div className="lg:col-span-4 space-y-6 lg:sticky lg:top-24">
          
          {/* Pricing & Stock Card */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100">
              Pricing &amp; Inventory
            </h2>

            {/* Regular Price (MRP) */}
            <div>
              <Label htmlFor="product-price" className="text-xs font-bold text-gray-800">
                Regular Price (MRP in ₹) *
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">₹</span>
                <Input
                  id="product-price"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.price}
                  onChange={(e) => handlePriceChange('price', e.target.value)}
                  placeholder="1999"
                  className="pl-8 text-sm font-semibold"
                  required
                />
              </div>
            </div>

            {/* Sale Price */}
            <div>
              <Label htmlFor="product-sale-price" className="text-xs font-bold text-gray-800">
                Sale Price (Discounted in ₹)
              </Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold text-sm">₹</span>
                <Input
                  id="product-sale-price"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.sale_price}
                  onChange={(e) => handlePriceChange('sale_price', e.target.value)}
                  placeholder="1299"
                  className="pl-8 text-sm font-semibold"
                />
              </div>
            </div>

            {/* Auto Discount Preview */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div>
                <Label htmlFor="product-discount" className="text-xs font-bold text-gray-800">
                  Discount %
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="product-discount"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount}
                    onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                    placeholder="35"
                    className="text-xs font-bold"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">%</span>
                </div>
              </div>

              <div>
                <Label htmlFor="product-stock" className="text-xs font-bold text-gray-800">
                  Available Quantity
                </Label>
                <Input
                  id="product-stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="100"
                  className="mt-1 text-xs font-bold"
                />
              </div>
            </div>
          </div>

          {/* Visibility & Badges Card */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-4">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100">
              Visibility &amp; Badges
            </h2>

            {/* Show/Hide Toggle (Point 5) */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200/70">
              <div className="flex items-center gap-2">
                {formData.is_active ? (
                  <Eye className="w-4 h-4 text-[#2E7D45]" />
                ) : (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                )}
                <div>
                  <p className="text-xs font-bold text-gray-900">Show on Store</p>
                  <p className="text-[10px] text-gray-500">{formData.is_active ? 'Visible to public customers' : 'Hidden from store'}</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 accent-[#6E1B34] rounded cursor-pointer"
              />
            </div>

            {/* Bestseller Toggle (Point 6) */}
            <div className="flex items-center justify-between p-3 bg-[#FCEEDF] rounded-xl border border-[#F2A65A]/40">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-[#D9711F] fill-current" />
                <div>
                  <p className="text-xs font-bold text-[#4E1225]">Bestseller Tag</p>
                  <p className="text-[10px] text-[#83725B]">Featured on Bestseller carousels</p>
                </div>
              </div>
              <input
                type="checkbox"
                checked={formData.bestseller}
                onChange={(e) => setFormData({ ...formData, bestseller: e.target.checked })}
                className="w-4 h-4 accent-[#6E1B34] rounded cursor-pointer"
              />
            </div>
          </div>

          {/* Gift with Purchase Card (Point 9) */}
          <div className="bg-white rounded-2xl p-6 border border-[#1A1A1A]/[0.08] shadow-xs space-y-3">
            <h2 className="font-heading text-base font-bold text-gray-900 pb-2 border-b border-gray-100 flex items-center gap-2">
              <Gift className="w-4 h-4 text-[#D9711F]" />
              Gift with Purchase (Bundle)
            </h2>
            <p className="text-[11px] text-gray-500">
              Assign another product as a free gift included with this purchase.
            </p>

            <select
              value={formData.gift_product_id}
              onChange={(e) => setFormData({ ...formData, gift_product_id: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-[#2A1F16] bg-white focus:outline-none focus:border-[#D9711F]"
            >
              <option value="">-- No Gift Product Assigned --</option>
              {allProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  🎁 {p.name} (₹{p.price})
                </option>
              ))}
            </select>
          </div>

          {/* Final Submit Button */}
          <Button
            type="submit"
            disabled={submitting}
            className="btn-saffron w-full py-3.5 text-sm font-bold rounded-xl shadow-lg flex items-center justify-center gap-2"
          >
            {submitting ? 'Saving Product...' : 'Publish Product'}
          </Button>

        </div>

      </div>

    </form>
  );
};

export default AdminProductAdd;
